// Feature: features/nma-appeal-letter-generator.md, features/nma-appeal-templates-demo-ui.md
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Button,
  ButtonGroup,
  Code,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  TableContainer,
  useDisclosure,
  useToast,
  HStack,
  VStack,
  Divider,
  Collapse,
} from '@chakra-ui/react';
import {
  ArrowBackIcon,
  RepeatIcon,
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  AddIcon,
  EditIcon,
  AttachmentIcon,
} from '@chakra-ui/icons';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import { formatCurrency } from './NMAClaims/shared';
import {
  loadAppealTemplates,
  loadClaimAppealSelections,
  saveClaimAppealSelection,
  matchTemplatesForClaim,
} from './NMAClaims/appealTemplatesStore';
import NMAAppealLaunchModal from './NMAClaims/NMAAppealLaunchModal';

const normalizeMarkdownText = (rawText) => {
  if (!rawText) return '';
  return String(rawText)
    .replace(/\$?\\rightarrow\$?/g, '->')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '    ');
};

const FormattedMarkdownBlock = ({ text, bg = 'white', p = 6 }) => {
  const normalized = normalizeMarkdownText(text);
  if (!normalized) return null;
  return (
    <Box
      bg={bg}
      p={p}
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      fontSize="sm"
      color="gray.800"
      lineHeight="1.7"
      sx={{
        '& p': { mb: 3, whiteSpace: 'pre-wrap', '&:last-child': { mb: 0 } },
        '& ul': { listStyleType: 'disc', pl: 6, mb: 3 },
        '& ol': { listStyleType: 'decimal', pl: 6, mb: 3 },
        '& li': { mb: 1.5, whiteSpace: 'pre-wrap' },
        '& h1': { fontSize: 'lg', fontWeight: 'bold', mt: 3, mb: 2, borderBottom: '1px solid #E2E8F0', pb: 1, color: '#1A365D' },
        '& h2': { fontSize: 'md', fontWeight: 'bold', mt: 3, mb: 2, color: '#1A365D' },
        '& h3, & h4': { fontSize: 'sm', fontWeight: 'bold', mt: 2.5, mb: 1.5, color: '#2D3748' },
        '& strong, & b': { fontWeight: 'bold' },
        '& blockquote': {
          borderLeft: '3px solid #3182CE',
          pl: 3,
          py: 1.5,
          my: 2.5,
          bg: 'blue.50',
          fontStyle: 'italic',
        },
        '& table': { width: '100%', borderCollapse: 'collapse', my: 3, fontSize: 'xs' },
        '& th, & td': { border: '1px solid #CBD5E0', px: 2.5, py: 1.5, textAlign: 'left', verticalAlign: 'top' },
        '& th': { bg: 'gray.100', fontWeight: 'bold', color: '#1A365D' },
        '& code': {
          bg: 'gray.100',
          px: 1,
          py: 0.5,
          borderRadius: 'sm',
          fontSize: '0.9em',
          fontFamily: 'mono',
        },
      }}
    >
      <ReactMarkdown>{normalized}</ReactMarkdown>
    </Box>
  );
};

const getAppealStatusBadge = (status) => {
  switch (status) {
    case 'APPEAL_READY':
      return <Badge colorScheme="green" px={2.5} py={1}>Appeal Letter Ready</Badge>;
    case 'JOB_PROCESSING':
      return (
        <Badge colorScheme="purple" px={2.5} py={1}>
          <HStack spacing={1.5} display="inline-flex" alignItems="center">
            <Spinner size="xs" speed="0.8s" />
            <Text as="span">Generating Appeal...</Text>
          </HStack>
        </Badge>
      );
    case 'JOB_PENDING':
      return <Badge colorScheme="blue" px={2.5} py={1}>Job Queued (Pending)</Badge>;
    case 'JOB_FAILED':
      return <Badge colorScheme="red" px={2.5} py={1}>Job Failed</Badge>;
    default:
      return <Badge colorScheme="orange" px={2.5} py={1}>Waiting on Appeal</Badge>;
  }
};

const NMAAppealDetail = () => {
  const { claimNumber } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const [claimData, setClaimData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isQueueingJob, setIsQueueingJob] = useState(false);

  // Client-side templates library & per-claim template selection
  const [templates] = useState(() => loadAppealTemplates());
  const [claimSelections, setClaimSelections] = useState(() => loadClaimAppealSelections());
  const {
    isOpen: isLaunchOpen,
    onOpen: onOpenLaunch,
    onClose: onCloseLaunch,
  } = useDisclosure();

  // PDF Blob URL state
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // View mode: 'split' | 'markdown' | 'pdf'
  const [viewMode, setViewMode] = useState('split');
  const [showLineItems, setShowLineItems] = useState(false);

  const fetchPdfPreview = useCallback(async (targetClaimNum, analysisId) => {
    if (!accessToken || !targetClaimNum) return;
    setLoadingPdf(true);
    setPdfError(null);
    try {
      const qs = analysisId ? `?analysis_id=${encodeURIComponent(analysisId)}` : '';
      const res = await callApi(
        `/api/nma/appeals/${encodeURIComponent(targetClaimNum)}/pdf${qs}`,
        accessToken
      );
      if (!res.ok) {
        throw new Error('Failed to render PDF preview');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl((prevUrl) => {
        if (prevUrl) URL.revokeObjectURL(prevUrl);
        return url;
      });
    } catch (err) {
      console.error('Error loading appeal PDF preview:', err);
      setPdfError(err.message);
    } finally {
      setLoadingPdf(false);
    }
  }, [accessToken]);

  const fetchClaimAppealDetail = useCallback(async (showSpinner = true) => {
    if (!accessToken || !claimNumber) return;
    if (showSpinner) setLoading(true);
    try {
      const res = await callApi(
        `/api/nma/appeals/${encodeURIComponent(claimNumber)}`,
        accessToken
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || `Failed to load appeal detail for ${claimNumber}`);
      }
      const claimObj = data.claim || null;
      setClaimData(claimObj);

      if (claimObj?.appeal_analysis?.id) {
        fetchPdfPreview(claimObj.claim_number, claimObj.appeal_analysis.id);
      }
    } catch (error) {
      console.error('Error fetching claim appeal detail:', error);
      toast({
        title: 'Error loading appeal detail',
        description: error.message,
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [accessToken, claimNumber, fetchPdfPreview, toast]);

  useEffect(() => {
    fetchClaimAppealDetail(true);
    return () => {
      setPdfBlobUrl((prevUrl) => {
        if (prevUrl) URL.revokeObjectURL(prevUrl);
        return null;
      });
    };
  }, [fetchClaimAppealDetail]);

  // Poll every 12 seconds while a job is pending or processing
  useEffect(() => {
    const status = claimData?.appeal_status;
    if (!accessToken || (status !== 'JOB_PENDING' && status !== 'JOB_PROCESSING')) {
      return undefined;
    }
    const interval = setInterval(() => {
      fetchClaimAppealDetail(false);
    }, 12000);
    return () => clearInterval(interval);
  }, [accessToken, claimData?.appeal_status, fetchClaimAppealDetail]);

  const handleConfirmLaunchAppeal = async ({
    claimRow,
    customInstructions,
    selectionMeta,
    force = false,
  }) => {
    if (!claimRow) return;
    setIsQueueingJob(true);
    try {
      const updatedSelections = saveClaimAppealSelection(claimRow.claim_number, selectionMeta);
      setClaimSelections(updatedSelections);

      const res = await callApi('/api/nma/appeals/jobs', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_number: claimRow.claim_number,
          case_id: claimRow.case_id,
          force,
          custom_instructions: customInstructions,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Failed to queue appeal job');
      }
      toast({
        title: data.already_queued
          ? 'Appeal Job Already Active'
          : selectionMeta?.submission_channel === 'PAYER_PORTAL_EAPPEAL'
          ? 'eAppeal Job Queued with Selected Templates'
          : 'Appeal Job Queued with Selected Templates',
        description: data.message,
        status: data.already_queued ? 'info' : 'success',
        duration: 4000,
        isClosable: true,
      });
      onCloseLaunch();
      await fetchClaimAppealDetail(false);
    } catch (err) {
      toast({
        title: 'Error queueing appeal job',
        description: err.message,
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setIsQueueingJob(false);
    }
  };

  const handleCopyMarkdown = (mdText) => {
    if (!mdText) return;
    navigator.clipboard.writeText(normalizeMarkdownText(mdText));
    toast({
      title: 'Copied to clipboard',
      description: 'Appeal letter Markdown copied.',
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
  };

  const handleDownloadMarkdown = (claimNum, mdText) => {
    if (!mdText) return;
    const blob = new Blob([normalizeMarkdownText(mdText)], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NMA_Appeal_Letter_${claimNum}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = () => {
    if (!pdfBlobUrl) return;
    const a = document.createElement('a');
    a.href = pdfBlobUrl;
    a.download = `NMA_Appeal_Letter_${claimData?.claim_number || claimNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const analysisObj = claimData?.appeal_analysis || null;
  const activeAnalysis = analysisObj?.analysis_result || {};
  const letterMarkdown = activeAnalysis.report_markdown || '';
  const appealMeta = activeAnalysis.appeal_metadata || activeAnalysis.header_block || {};
  const hasActiveJob =
    claimData?.appeal_status === 'JOB_PENDING' || claimData?.appeal_status === 'JOB_PROCESSING';
  const lineItems = claimData?.active_line_items || [];

  const savedSelection = useMemo(() => {
    const key = String(claimData?.claim_number || claimNumber || '').toUpperCase();
    return claimSelections[key] || null;
  }, [claimSelections, claimData?.claim_number, claimNumber]);

  const matchInfo = useMemo(() => {
    if (!claimData) return null;
    return matchTemplatesForClaim(claimData, templates, savedSelection?.procedure_type || '');
  }, [claimData, templates, savedSelection?.procedure_type]);

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />

      <Box maxW="1720px" mx="auto" px={6} py={5}>
        {/* Top Action & Navigation Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={4}>
          <HStack spacing={3} wrap="wrap">
            <Button
              size="sm"
              variant="outline"
              leftIcon={<ArrowBackIcon />}
              onClick={() => navigate('/nma-appeals')}
            >
              Back to Appeals Queue
            </Button>

            <Button
              size="sm"
              variant="outline"
              colorScheme="purple"
              leftIcon={<EditIcon />}
              onClick={() => navigate('/nma-appeals?tab=templates')}
            >
              Templates Library
            </Button>

            <Heading size="md" color="gray.800">
              Appeal Letter: {claimData?.claim_number || claimNumber}
            </Heading>

            {claimData && (
              <>
                <Badge colorScheme={claimData.claim_type === 'pro' ? 'blue' : 'purple'}>
                  {(claimData.claim_type || 'pro').toUpperCase()}
                </Badge>
                <Badge colorScheme="teal" fontSize="xs">
                  {claimData.payer_name}
                </Badge>
                {getAppealStatusBadge(claimData.appeal_status)}
              </>
            )}
          </HStack>

          <HStack spacing={2} wrap="wrap">
            {analysisObj && (
              <>
                <ButtonGroup size="xs" isAttached variant="outline">
                  <Button
                    colorScheme={viewMode === 'split' ? 'blue' : 'gray'}
                    variant={viewMode === 'split' ? 'solid' : 'outline'}
                    onClick={() => setViewMode('split')}
                  >
                    Split View (Letter + PDF)
                  </Button>
                  <Button
                    colorScheme={viewMode === 'markdown' ? 'blue' : 'gray'}
                    variant={viewMode === 'markdown' ? 'solid' : 'outline'}
                    onClick={() => setViewMode('markdown')}
                  >
                    Formatted Letter Only
                  </Button>
                  <Button
                    colorScheme={viewMode === 'pdf' ? 'blue' : 'gray'}
                    variant={viewMode === 'pdf' ? 'solid' : 'outline'}
                    onClick={() => setViewMode('pdf')}
                  >
                    PDF Preview Only
                  </Button>
                </ButtonGroup>

                <Button
                  size="xs"
                  colorScheme="red"
                  leftIcon={<DownloadIcon />}
                  onClick={handleDownloadPdf}
                  isDisabled={!pdfBlobUrl}
                >
                  Download PDF
                </Button>

                {pdfBlobUrl && (
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="blue"
                    leftIcon={<ExternalLinkIcon />}
                    onClick={() => window.open(pdfBlobUrl, '_blank', 'noopener,noreferrer')}
                  >
                    Open PDF Tab
                  </Button>
                )}

                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="gray"
                  leftIcon={<CopyIcon />}
                  onClick={() => handleCopyMarkdown(letterMarkdown)}
                >
                  Copy Markdown
                </Button>

                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="green"
                  leftIcon={<DownloadIcon />}
                  onClick={() => handleDownloadMarkdown(claimData?.claim_number || claimNumber, letterMarkdown)}
                >
                  Download .md
                </Button>
              </>
            )}

            <Button
              size="xs"
              colorScheme={analysisObj ? 'purple' : 'blue'}
              leftIcon={analysisObj ? <RepeatIcon /> : <AddIcon />}
              isLoading={isQueueingJob}
              isDisabled={hasActiveJob}
              onClick={onOpenLaunch}
            >
              {hasActiveJob
                ? 'Job Running...'
                : analysisObj
                ? 'Review Templates & Regenerate'
                : 'Review Templates & Create Appeal'}
            </Button>
          </HStack>
        </Flex>

        {loading ? (
          <Flex justify="center" align="center" py={20}>
            <Spinner size="xl" color="blue.500" mr={4} />
            <Text color="gray.600">Loading claim details and appeal letter...</Text>
          </Flex>
        ) : !claimData ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            Could not find claim {claimNumber}.
          </Alert>
        ) : (
          <VStack align="stretch" spacing={4}>
            {/* Claim Metadata & Triage Summary Card */}
            <Card variant="outline" bg="white" borderTop="3px solid #3182CE">
              <CardBody py={3.5} px={5}>
                <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4} mb={3}>
                  <HStack spacing={6} wrap="wrap" fontSize="xs" color="gray.700">
                    <Box>
                      <Text color="gray.500" fontSize="10px" textTransform="uppercase">NMA Case / Patient</Text>
                      <Text fontWeight="bold">
                        Case #{claimData.nma_case_no} {claimData.patient_name ? `• ${claimData.patient_name}` : ''}
                      </Text>
                    </Box>
                    <Box>
                      <Text color="gray.500" fontSize="10px" textTransform="uppercase">Date of Service / POS</Text>
                      <Text fontWeight="bold">
                        {claimData.date_of_service || 'N/A'} (POS {claimData.place_of_service || 'UNK'})
                      </Text>
                    </Box>
                    <Box>
                      <Text color="gray.500" fontSize="10px" textTransform="uppercase">Facility / Physician</Text>
                      <Text fontWeight="bold">
                        {claimData.facility_name || 'N/A'}{' '}
                        {claimData.rendering_provider ? `• ${claimData.rendering_provider}` : ''}
                      </Text>
                    </Box>
                    <Box>
                      <Text color="gray.500" fontSize="10px" textTransform="uppercase">Payer ICN</Text>
                      <Text fontWeight="bold" fontFamily="mono">
                        {claimData.payer_claim_control_number || 'N/A'}
                      </Text>
                    </Box>
                    <Box>
                      <Text color="gray.500" fontSize="10px" textTransform="uppercase">Denied Core CPTs / CARC</Text>
                      <HStack spacing={1} mt={0.5}>
                        {(claimData.denied_core_cpts?.length
                          ? claimData.denied_core_cpts
                          : claimData.denied_cpts || []
                        ).map((c) => (
                          <Badge key={c} colorScheme="red" fontSize="10px">{c}</Badge>
                        ))}
                        {claimData.top_carc && (
                          <Badge colorScheme="orange" fontSize="10px">
                            {claimData.top_carc}{claimData.top_rarc ? ` / ${claimData.top_rarc}` : ''}
                          </Badge>
                        )}
                      </HStack>
                    </Box>
                    <Box>
                      <Text color="gray.500" fontSize="10px" textTransform="uppercase">Core Denied Amount (beta)</Text>
                      <Text fontWeight="bold" color="red.600">
                        {formatCurrency(claimData.core_denied_amount)}
                      </Text>
                    </Box>
                  </HStack>

                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    rightIcon={showLineItems ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setShowLineItems((prev) => !prev)}
                  >
                    {showLineItems ? 'Hide Claim Lines & ICDs' : `Show Claim Lines (${lineItems.length}) & ICDs`}
                  </Button>
                </Flex>

                {/* Matched / Applied Templates Strip */}
                {matchInfo && (
                  <Flex
                    p={2.5}
                    mb={3}
                    bg="blue.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.200"
                    justify="space-between"
                    align="center"
                    wrap="wrap"
                    gap={2}
                  >
                    <HStack spacing={2} wrap="wrap">
                      <Text fontSize="xs" fontWeight="bold" color="blue.900">
                        Matched Templates &amp; Procedure:
                      </Text>
                      <Badge colorScheme={matchInfo.inferredProcedure.colorScheme || 'blue'} fontSize="10px">
                        {savedSelection?.procedure_type || matchInfo.inferredProcedure.shortLabel}
                      </Badge>
                      {(savedSelection?.template_names && savedSelection.template_names.length > 0
                        ? savedSelection.template_names
                        : matchInfo.matchedTemplates.map((t) => t.name)
                      ).map((tName) => (
                        <Badge key={tName} colorScheme="green" variant="outline" bg="white" fontSize="10px">
                          ✓ {tName}
                        </Badge>
                      ))}
                      {matchInfo.matchedTemplates.some((t) => (t.attached_files || []).length > 0) && (
                        <Badge colorScheme="purple" variant="subtle" fontSize="10px">
                          <AttachmentIcon mr={1} boxSize={2.5} />
                          {matchInfo.matchedTemplates.reduce(
                            (acc, t) => acc + (t.attached_files?.length || 0),
                            0
                          )}{' '}
                          Policy / Sample File(s)
                        </Badge>
                      )}
                    </HStack>

                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      bg="white"
                      leftIcon={<EditIcon />}
                      onClick={onOpenLaunch}
                    >
                      Review / Change Templates
                    </Button>
                  </Flex>
                )}

                <Collapse in={showLineItems} animateOpacity>
                  <Box pt={2} pb={3} borderTop="1px solid" borderColor="gray.100">
                    {claimData.billed_icd10_codes?.length > 0 && (
                      <HStack spacing={2} mb={2.5} wrap="wrap">
                        <Text fontSize="xs" fontWeight="bold" color="gray.600">
                          Billed ICD-10 Sequence:
                        </Text>
                        {claimData.billed_icd10_codes.map((icd, idx) => (
                          <Badge key={`${icd}-${idx}`} colorScheme={idx === 0 ? 'blue' : 'gray'} fontSize="10px">
                            {String.fromCharCode(65 + idx)}: {icd}
                          </Badge>
                        ))}
                      </HStack>
                    )}

                    {lineItems.length > 0 && (
                      <TableContainer borderWidth="1px" borderRadius="md" borderColor="gray.200">
                        <Table size="sm" variant="simple">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th fontSize="10px">CPT</Th>
                              <Th fontSize="10px">Modifiers</Th>
                              <Th fontSize="10px" isNumeric>Units</Th>
                              <Th fontSize="10px">Pointers</Th>
                              <Th fontSize="10px" isNumeric>Billed (beta)</Th>
                              <Th fontSize="10px" isNumeric>Paid (beta)</Th>
                              <Th fontSize="10px" isNumeric>Denied (beta)</Th>
                              <Th fontSize="10px">CARC / RARC</Th>
                              <Th fontSize="10px">Remittance Description</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {lineItems.map((li, idx) => (
                              <Tr key={`${li.cpt_code}-${idx}`}>
                                <Td fontWeight="bold" fontSize="xs">{li.cpt_code}</Td>
                                <Td fontSize="xs">{(li.modifiers || []).join(', ') || '—'}</Td>
                                <Td fontSize="xs" isNumeric>{li.units ?? 1}</Td>
                                <Td fontSize="xs">{(li.diag_pointers || []).join(', ') || '—'}</Td>
                                <Td fontSize="xs" isNumeric>{formatCurrency(li.billed_amount)}</Td>
                                <Td fontSize="xs" isNumeric color={li.paid_amount > 0 ? 'green.600' : 'gray.500'}>
                                  {formatCurrency(li.paid_amount)}
                                </Td>
                                <Td fontSize="xs" isNumeric color={li.denied_amount > 0 ? 'red.600' : 'gray.500'}>
                                  {formatCurrency(li.denied_amount)}
                                </Td>
                                <Td fontSize="xs">
                                  {li.carc ? (
                                    <Badge colorScheme="orange" fontSize="10px">
                                      {li.carc}{li.rarc ? ` / ${li.rarc}` : ''}
                                    </Badge>
                                  ) : (
                                    '—'
                                  )}
                                </Td>
                                <Td fontSize="11px" color="gray.600" maxW="360px" isTruncated title={li.description || ''}>
                                  {li.description || li.denial_category || '—'}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Collapse>

                {/* AI Triage Summary (Headline, Root Cause, Recommendation) */}
                {(activeAnalysis.headline || activeAnalysis.root_cause_summary || activeAnalysis.recommendation) && (
                  <Box pt={3} borderTop="1px solid" borderColor="gray.100">
                    {activeAnalysis.headline && (
                      <Heading size="xs" color="blue.800" mb={2.5}>
                        {activeAnalysis.headline}
                      </Heading>
                    )}

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                      {activeAnalysis.root_cause_summary && (
                        <Box>
                          <Text fontSize="11px" fontWeight="bold" color="gray.600" textTransform="uppercase" mb={1}>
                            Denial Root Cause & Clinical / Policy Basis
                          </Text>
                          <FormattedMarkdownBlock text={activeAnalysis.root_cause_summary} bg="gray.50" p={3.5} />
                        </Box>
                      )}
                      {activeAnalysis.recommendation && (
                        <Box>
                          <Text fontSize="11px" fontWeight="bold" color="gray.600" textTransform="uppercase" mb={1}>
                            Recommended Action & Enclosures
                          </Text>
                          <FormattedMarkdownBlock text={activeAnalysis.recommendation} bg="blue.50" p={3.5} />
                        </Box>
                      )}
                    </SimpleGrid>

                    {(appealMeta.submission_destination ||
                      appealMeta.appeals_destination ||
                      claimData.recent_denial_doc?.submission_destination) && (
                      <Text fontSize="xs" color="gray.700" mt={2.5}>
                        <strong>Submission Destination:</strong>{' '}
                        <Code fontSize="xs">
                          {appealMeta.submission_destination ||
                            appealMeta.appeals_destination ||
                            claimData.recent_denial_doc?.submission_destination}
                        </Code>
                      </Text>
                    )}
                  </Box>
                )}
              </CardBody>
            </Card>

            {/* Main Appeal Letter Content & PDF Preview Workspace */}
            {!analysisObj ? (
              <Card variant="outline" bg="white">
                <CardBody py={12} textAlign="center">
                  {hasActiveJob ? (
                    <VStack spacing={3}>
                      <Spinner size="lg" color="purple.500" />
                      <Heading size="sm" color="gray.700">
                        Appeal Letter Job is Currently {claimData.appeal_status === 'JOB_PROCESSING' ? 'Running' : 'Queued'}
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        This page will automatically refresh as soon as the appeal letter is generated.
                      </Text>
                    </VStack>
                  ) : (
                    <VStack spacing={3}>
                      <Text fontSize="sm" color="gray.600">
                        No appeal letter has been generated for <strong>{claimData.claim_number}</strong> yet.
                      </Text>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<AddIcon />}
                        isLoading={isQueueingJob}
                        onClick={onOpenLaunch}
                      >
                        Review Templates &amp; Create Appeal Job
                      </Button>
                    </VStack>
                  )}
                </CardBody>
              </Card>
            ) : (
              <SimpleGrid
                columns={{
                  base: 1,
                  xl: viewMode === 'split' ? 2 : 1,
                }}
                spacing={5}
                alignItems="stretch"
              >
                {/* Left Pane: Formatted Markdown Appeal Letter */}
                {(viewMode === 'split' || viewMode === 'markdown') && (
                  <Card variant="outline" bg="white" shadow="sm">
                    <CardHeader py={3} px={5} borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
                      <Flex justify="space-between" align="center">
                        <Heading size="xs" color="gray.700" textTransform="uppercase">
                          Formatted Appeal Letter (Markdown)
                        </Heading>
                        <HStack spacing={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<CopyIcon />}
                            onClick={() => handleCopyMarkdown(letterMarkdown)}
                          >
                            Copy
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="green"
                            leftIcon={<DownloadIcon />}
                            onClick={() => handleDownloadMarkdown(claimData.claim_number, letterMarkdown)}
                          >
                            .md
                          </Button>
                        </HStack>
                      </Flex>
                    </CardHeader>
                    <CardBody p={4} maxH="920px" overflowY="auto">
                      <FormattedMarkdownBlock text={letterMarkdown} bg="white" p={6} />
                    </CardBody>
                  </Card>
                )}

                {/* Right Pane: Embedded PDF Preview */}
                {(viewMode === 'split' || viewMode === 'pdf') && (
                  <Card variant="outline" bg="white" shadow="sm">
                    <CardHeader py={3} px={5} borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
                      <Flex justify="space-between" align="center">
                        <HStack spacing={2}>
                          <Heading size="xs" color="gray.700" textTransform="uppercase">
                            PDF Letter Preview
                          </Heading>
                          <Badge colorScheme="red" fontSize="10px">PDF</Badge>
                        </HStack>
                        <HStack spacing={2}>
                          {pdfBlobUrl && (
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="blue"
                              leftIcon={<ExternalLinkIcon />}
                              onClick={() => window.open(pdfBlobUrl, '_blank', 'noopener,noreferrer')}
                            >
                              Full Screen PDF
                            </Button>
                          )}
                          <Button
                            size="xs"
                            colorScheme="red"
                            leftIcon={<DownloadIcon />}
                            onClick={handleDownloadPdf}
                            isDisabled={!pdfBlobUrl}
                          >
                            Download PDF
                          </Button>
                        </HStack>
                      </Flex>
                    </CardHeader>
                    <CardBody p={0} bg="gray.700" minH="880px">
                      {loadingPdf ? (
                        <Flex justify="center" align="center" h="880px" color="white">
                          <Spinner size="lg" mr={3} />
                          <Text>Rendering PDF letter...</Text>
                        </Flex>
                      ) : pdfError ? (
                        <Box p={6}>
                          <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            {pdfError}
                          </Alert>
                        </Box>
                      ) : pdfBlobUrl ? (
                        <Box
                          as="iframe"
                          title={`Appeal Letter PDF - ${claimData.claim_number}`}
                          src={pdfBlobUrl}
                          width="100%"
                          height="900px"
                          style={{ border: 'none', display: 'block' }}
                        />
                      ) : null}
                    </CardBody>
                  </Card>
                )}
              </SimpleGrid>
            )}
          </VStack>
        )}
      </Box>

      {/* Pre-Flight Template Review & Launch Modal */}
      <NMAAppealLaunchModal
        isOpen={isLaunchOpen}
        onClose={onCloseLaunch}
        claimRow={claimData}
        templates={templates}
        savedSelection={savedSelection}
        isSubmitting={isQueueingJob}
        forceRegenerate={Boolean(analysisObj)}
        onConfirmLaunch={handleConfirmLaunchAppeal}
        onSwitchToTemplatesTab={() => navigate('/nma-appeals?tab=templates')}
      />
    </Box>
  );
};

export default NMAAppealDetail;
